import { MdCheckBox, MdCheckBoxOutlineBlank, MdFileDownload } from 'react-icons/md';
import { Appearance } from '@Components/designConstants';
import { Header } from '@Components/StickyTable';
import { IconButton } from '@Components/IconButton';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { CreditMemoRow, getCreditMemoReference } from './credit-memos-utils';

export const PAGE_TITLE = 'Credit Memos';
export const PAGE_TYPE = 'Credit Memos';
export const DATE_FORMAT = 'yyyy-MM-dd';
export const ROW_HEIGHT = 26;
export const HEADER_HEIGHT = 40;

const WIDTHS = {
  SELECTED: 40,
  PDF: 50
} as const;

interface BuildCreditMemoHeadersParams {
  onToggleSelect: (row: CreditMemoRow) => void;
  onDownloadPDF: (row: CreditMemoRow) => void;
}

export const buildCreditMemoHeaders = ({
  onToggleSelect,
  onDownloadPDF
}: BuildCreditMemoHeadersParams): Header[] => [
  {
    label: '',
    name: 'selected',
    width: WIDTHS.SELECTED,
    nonInteractive: true,
    renderer: (row: CreditMemoRow) => (
      <IconButton
        appearance={Appearance.EMPTY}
        onClick={() => onToggleSelect(row)}
        data-testid={`select-row-${row.id}`}
      >
        {row.selected ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
      </IconButton>
    )
  },
  {
    label: 'Reference',
    name: 'reference',
    flex: true,
    minFlexWidth: 360,
    // Not a real field on the row -- contentGetter feeds the built-in filter bar/sort/CSV the
    // same computed string the renderer displays.
    contentGetter: getCreditMemoReference,
    renderer: getCreditMemoReference
  },
  {
    // `creationDatetime` isn't part of bpm-types' CreditMemoDataRow, so this field is untyped
    // and silently renders blank if the backend's actual field name ever drifts. Left as-is --
    // adding it properly means a bpm-types change in a separate repo, out of scope here.
    label: 'Creation Time (Eastern)',
    name: 'creationDatetime',
    flex: true,
    minFlexWidth: 200
  },
  {
    label: 'Week',
    name: 'week',
    sortPriority: 1,
    sortAscending: true,
    flex: true,
    // Excluded from the filter bar's own options -- the date range picker already controls
    // which weeks are in view.
    nonFilterable: true
  },
  {
    label: 'Network',
    name: 'network',
    flex: true,
    minFlexWidth: 160,
    sortPriority: 2,
    sortAscending: true
  },
  {
    label: 'Avail',
    name: 'avail',
    sortPriority: 3,
    sortAscending: true,
    flex: true,
    minFlexWidth: 60
  },
  { label: 'Company', name: 'company', sortPriority: 4, sortAscending: true, flex: true },
  {
    label: 'Approved Credit Amount',
    name: 'approvedCreditAmount',
    sortPriority: 5,
    sortAscending: true,
    flex: true,
    renderer: (row: CreditMemoRow) => <NumberFormatter value={row.approvedCreditAmount} type='$' />
  },
  {
    label: 'Outstanding Credit Amount',
    name: 'creditAmount',
    sortPriority: 6,
    sortAscending: true,
    flex: true,
    renderer: (row: CreditMemoRow) => <NumberFormatter value={row.creditAmount} type='$' />
  },
  {
    label: 'Number of Spots',
    name: 'nSpots',
    sortPriority: 7,
    sortAscending: true,
    flex: true
  },
  { label: 'Status', name: 'status', sortPriority: 8, sortAscending: true, flex: true },
  {
    label: 'PDF',
    name: 'pdf',
    nonInteractive: true,
    width: WIDTHS.PDF,
    renderer: (row: CreditMemoRow) => (
      <IconButton
        appearance={Appearance.EMPTY}
        loading={row.fetching}
        onClick={() => onDownloadPDF(row)}
        data-testid={`download-pdf-${row.id}`}
      >
        <MdFileDownload />
      </IconButton>
    )
  }
];
